import React, { useState, useEffect, ChangeEvent } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Autocomplete,
  Chip,
} from "@mui/material";
import * as Yup from "yup";
import { PromoCategory, PromoType } from "../CreatePromo/CreatePromo";

export type PromoCodeStatus = "ACTIVE" | "INACTIVE" | "EXPIRED" | "ARCHIVED";

export interface UpdatePromoPayload {
  code?: string;
  category?: PromoCategory;
  type?: PromoType;
  title?: string | null;
  description?: string | null;
  user_promo_credits?: number | null;
  org_promo_credits?: number | null;
  tnc?: string | null;
  max_usage?: number | null;
  max_usage_per_account?: number | null;
  status?: PromoCodeStatus;
  start_at?: string | null;
  expire_at?: string | null;
  valid_duration?: number | null;
  valid_for_users?: string[];
  valid_for_orgs?: string[];
}

interface UpdatePromoProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UpdatePromoPayload, params: string) => void;
  promoId: number;
  initialData: UpdatePromoPayload;
}

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .trim()
    .uppercase()
    .min(1, "Code is too short")
    .max(10, "Code is too long")
    .required("Code is required"),
  title: Yup.string().max(255, "Title too long").nullable(),
  description: Yup.string().nullable(),
  category: Yup.mixed<PromoCategory>()
    .oneOf(["PROMO", "EVALUATION", "PAID"], "Invalid category")
    .required("Category is required"),
  type: Yup.mixed<PromoType>()
    .when("category", {
      is: "EVALUATION",
      then: (schema) =>
        schema
          .oneOf(
            ["ORGANIZATION", "ORG_SPECEFIC"],
            "Evaluation supports only Organization or Org Specific"
          )
          .required("Type is required"),
      otherwise: (schema) =>
        schema
          .oneOf(
            ["USER", "USER_SPECIFIC", "ORGANIZATION", "ORG_SPECEFIC", "ALL"],
            "Invalid type"
          )
          .required("Type is required"),
    })
    .required("Type is required"),
  user_promo_credits: Yup.number()
    .nullable()
    .when("type", {
      is: (val: PromoType) =>
        val === "USER" || val === "ALL" || val === "USER_SPECIFIC",
      then: (schema) =>
        schema.required("User credits required").min(0, "Must be at least 0"),
      otherwise: (schema) => schema.nullable(),
    }),
  org_promo_credits: Yup.number()
    .nullable()
    .when("type", {
      is: (val: PromoType) =>
        val === "ORGANIZATION" || val === "ALL" || val === "ORG_SPECEFIC",
      then: (schema) =>
        schema.required("Org credits required").min(0, "Must be at least 0"),
      otherwise: (schema) => schema.nullable(),
    }),
  tnc: Yup.string().nullable().max(500, "T&C too long"),
  max_usage: Yup.number().nullable().min(0, "Must be at least 0"),
  max_usage_per_account: Yup.number().nullable().min(0, "Must be at least 0"),
  status: Yup.mixed<PromoCodeStatus>()
    .oneOf(["ACTIVE", "INACTIVE", "EXPIRED", "ARCHIVED"], "Invalid status")
    .required("Status is required"),
  start_at: Yup.date().nullable().typeError("Invalid start date"),
  expire_at: Yup.date()
    .nullable()
    .min(Yup.ref("start_at"), "Expire must be after start")
    .typeError("Invalid expire date"),
  valid_duration: Yup.number()
    .nullable()
    .when("category", {
      is: "EVALUATION",
      then: (schema) =>
        schema
          .integer("Must be a whole number")
          .moreThan(0, "Must be greater than 0"),
      otherwise: (schema) => schema.nullable(),
    }),
  valid_for_users: Yup.array()
    .of(Yup.string().email("Invalid email"))
    .when("type", {
      is: (val: PromoType) => val === "USER_SPECIFIC",
      then: (schema) => schema.min(1, "Add at least one user email"),
      otherwise: (schema) => schema.optional(),
    }),
  valid_for_orgs: Yup.array()
    .of(Yup.string().email("Invalid email"))
    .when(["type", "category"], {
      is: (type: PromoType, category: PromoCategory) =>
        type === "ORG_SPECEFIC" || category === "EVALUATION",
      then: (schema) => schema.min(1, "Add at least one organization email"),
      otherwise: (schema) => schema.optional(),
    }),
});

const UpdatePromo: React.FC<UpdatePromoProps> = ({
  open,
  onClose,
  onSubmit,
  promoId,
  initialData,
}) => {
  const [values, setValues] = useState<{
    code: string;
    category: PromoCategory;
    type: PromoType;
    title: string;
    description: string;
    user_promo_credits: string;
    org_promo_credits: string;
    tnc: string;
    max_usage: string;
    max_usage_per_account: string;
    status: PromoCodeStatus;
    start_at: string;
    expire_at: string;
    valid_duration: string;
    valid_for_users: string[];
    valid_for_orgs: string[];
  }>({
    code: "",
    category: "PROMO",
    type: "USER",
    title: "",
    description: "",
    user_promo_credits: "",
    org_promo_credits: "",
    tnc: "",
    max_usage: "",
    max_usage_per_account: "",
    status: "ACTIVE",
    start_at: "",
    expire_at: "",
    valid_duration: "",
    valid_for_users: [],
    valid_for_orgs: [],
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof UpdatePromoPayload, string>>
  >({});

  useEffect(() => {
    const toLocalDateTime = (iso?: string | null) => {
      if (!iso) return "";
      const d = new Date(iso);
      const tzOffMs = d.getTimezoneOffset() * 60000;
      const local = new Date(d.getTime() - tzOffMs);
      return local.toISOString().slice(0, 16);
    };

    setValues({
      code: initialData.code ?? "",
      category: initialData.category ?? "PROMO",
      type: initialData.type ?? "USER",
      title: initialData.title ?? "",
      description: initialData.description ?? "",
      user_promo_credits:
        initialData.user_promo_credits != null
          ? String(initialData.user_promo_credits)
          : "",
      org_promo_credits:
        initialData.org_promo_credits != null
          ? String(initialData.org_promo_credits)
          : "",
      tnc: initialData.tnc ?? "",
      max_usage:
        initialData.max_usage != null ? String(initialData.max_usage) : "",
      max_usage_per_account:
        initialData.max_usage_per_account != null
          ? String(initialData.max_usage_per_account)
          : "",
      status: initialData.status ?? "ACTIVE",
      start_at: toLocalDateTime(initialData.start_at),
      expire_at: toLocalDateTime(initialData.expire_at),
      valid_duration:
        initialData.valid_duration != null
          ? String(initialData.valid_duration)
          : "",
      valid_for_users: initialData.valid_for_users ?? [],
      valid_for_orgs: initialData.valid_for_orgs ?? [],
    });
    setErrors({});
  }, [initialData, open]);

  const numberFields: (keyof UpdatePromoPayload | "valid_duration")[] = [
    "user_promo_credits",
    "org_promo_credits",
    "max_usage",
    "max_usage_per_account",
    "valid_duration",
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (!name) return;
    const isNumberField = numberFields.includes(name as any);
    let val: string = value;
    if (type === "number" && isNumberField) {
      val = value === "" ? "" : String(Number(value));
    }
    setValues((prev) => ({ ...prev, [name]: val }));
  };

  const handleCategoryChange = (category: PromoCategory) => {
    const allowedTypes: PromoType[] =
      category === "EVALUATION"
        ? ["ORGANIZATION", "ORG_SPECEFIC"]
        : ["USER", "USER_SPECIFIC", "ORGANIZATION", "ORG_SPECEFIC", "ALL"];

    setValues((prev) => ({
      ...prev,
      category,
      type: allowedTypes.includes(prev.type) ? prev.type : allowedTypes[0],
      valid_for_users: category === "EVALUATION" ? [] : prev.valid_for_users,
      valid_for_orgs: prev.valid_for_orgs,
      valid_duration: category === "EVALUATION" ? prev.valid_duration : "",
    }));
  };

  const handleTypeChange = (type: PromoType) => {
    setValues((prev) => ({
      ...prev,
      type,
      valid_for_users: type === "USER_SPECIFIC" ? prev.valid_for_users : [],
      valid_for_orgs:
        type === "ORG_SPECEFIC" || prev.category === "EVALUATION"
          ? prev.valid_for_orgs
          : [],
    }));
  };

  const showUserCredits =
    values.type === "USER" ||
    values.type === "ALL" ||
    values.type === "USER_SPECIFIC";
  const showOrgCredits =
    values.type === "ORGANIZATION" ||
    values.type === "ALL" ||
    values.type === "ORG_SPECEFIC";
  const showUserEmails = values.type === "USER_SPECIFIC";
  const showOrgEmails =
    values.type === "ORG_SPECEFIC" || values.category === "EVALUATION";

  const formatLabel = (option: string) =>
    option
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());

  const handleSubmit = async () => {
    try {
      const raw = {
        code: values.code,
        category: values.category,
        type: values.type,
        title: values.title?.trim() ? values.title.trim() : null,
        description: values.description?.trim() ? values.description.trim() : null,
        user_promo_credits:
          values.user_promo_credits !== ""
            ? Number(values.user_promo_credits)
            : null,
        org_promo_credits:
          values.org_promo_credits !== ""
            ? Number(values.org_promo_credits)
            : null,
        tnc: values.tnc || null,
        max_usage: values.max_usage !== "" ? Number(values.max_usage) : null,
        max_usage_per_account:
          values.max_usage_per_account !== ""
            ? Number(values.max_usage_per_account)
            : null,
        status: values.status,
        start_at: values.start_at
          ? new Date(values.start_at).toISOString()
          : null,
        expire_at: values.expire_at
          ? new Date(values.expire_at).toISOString()
          : null,
        valid_duration:
          values.valid_duration !== "" ? Number(values.valid_duration) : null,
        valid_for_users: values.valid_for_users,
        valid_for_orgs: values.valid_for_orgs,
      };

      const valid = await validationSchema.validate(raw, {
        abortEarly: false,
      });
      setErrors({});

      const payload: UpdatePromoPayload = {
        code: valid.code,
        category: valid.category,
        type: valid.type,
        title: (valid.title as string | null) ?? null,
        description: (valid.description as string | null) ?? null,
        user_promo_credits: valid.user_promo_credits,
        org_promo_credits: valid.org_promo_credits,
        tnc: valid.tnc ?? null,
        max_usage: valid.max_usage ?? null,
        max_usage_per_account: valid.max_usage_per_account ?? null,
        status: valid.status,
        start_at: valid.start_at
          ? (valid.start_at as Date).toISOString()
          : null,
        expire_at: valid.expire_at
          ? (valid.expire_at as Date).toISOString()
          : null,
      };

      if (valid.category === "EVALUATION") {
        payload.valid_duration = valid.valid_duration ?? null;
      }

      if (valid.valid_for_users?.length) {
        payload.valid_for_users = valid.valid_for_users.filter(
          (v): v is string => Boolean(v)
        );
      }
      if (valid.valid_for_orgs?.length) {
        payload.valid_for_orgs = valid.valid_for_orgs.filter((v): v is string =>
          Boolean(v)
        );
      }

      onSubmit(payload, `${promoId}/`);
    } catch (err: any) {
      const formErrors: any = {};
      if (Array.isArray(err.inner)) {
        err.inner.forEach((ve: any) => {
          if (ve.path) formErrors[ve.path] = ve.message;
        });
      } else if (err.path) {
        formErrors[err.path] = err.message;
      }
      setErrors(formErrors);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Promo</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="code"
            label="Promo Code"
            value={values.code}
            onChange={handleChange}
            error={Boolean(errors.code)}
            helperText={errors.code}
            fullWidth
          />
          <TextField
            name="title"
            label="Title (shown on billing)"
            value={values.title}
            onChange={handleChange}
            error={Boolean(errors.title)}
            helperText={errors.title}
            fullWidth
          />
          <TextField
            name="description"
            label="Description (shown on billing)"
            value={values.description}
            onChange={handleChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
            multiline
            rows={2}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              name="category"
              value={values.category}
              onChange={(e) =>
                handleCategoryChange(e.target.value as PromoCategory)
              }
              error={Boolean(errors.category)}
              label="Category"
            >
              <MenuItem value="PROMO">Promo</MenuItem>
              <MenuItem value="EVALUATION">Evaluation</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
            </Select>
            {errors.category && (
              <Box sx={{ color: "error.main" }}>{errors.category}</Box>
            )}
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              name="type"
              value={values.type}
              onChange={(e) => handleTypeChange(e.target.value as PromoType)}
              error={Boolean(errors.type)}
              label="Type"
            >
              {(values.category === "EVALUATION"
                ? ["ORGANIZATION", "ORG_SPECEFIC"]
                : [
                    "USER",
                    "USER_SPECIFIC",
                    "ORGANIZATION",
                    "ORG_SPECEFIC",
                    "ALL",
                  ]
              ).map((option) => (
                <MenuItem key={option} value={option}>
                  {formatLabel(option)}
                </MenuItem>
              ))}
            </Select>
            {errors.type && (
              <Box sx={{ color: "error.main" }}>{errors.type}</Box>
            )}
          </FormControl>
          {showUserCredits && (
            <TextField
              name="user_promo_credits"
              label="User Credits"
              type="number"
              inputProps={{ min: 0 }}
              value={values.user_promo_credits}
              onChange={handleChange}
              error={Boolean(errors.user_promo_credits)}
              helperText={errors.user_promo_credits}
              fullWidth
            />
          )}
          {showOrgCredits && (
            <TextField
              name="org_promo_credits"
              label="Org Credits"
              type="number"
              inputProps={{ min: 0 }}
              value={values.org_promo_credits}
              onChange={handleChange}
              error={Boolean(errors.org_promo_credits)}
              helperText={errors.org_promo_credits}
              fullWidth
            />
          )}
          {values.category === "EVALUATION" && (
            <TextField
              name="valid_duration"
              label="Validity Duration (days)"
              type="number"
              inputProps={{ min: 1 }}
              value={values.valid_duration}
              onChange={handleChange}
              error={Boolean(errors.valid_duration)}
              helperText={errors.valid_duration}
              fullWidth
            />
          )}
          {showUserEmails && (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={values.valid_for_users}
              onChange={(_, newValue) =>
                setValues((prev) => ({
                  ...prev,
                  valid_for_users: newValue
                    .map((item) => item.trim())
                    .filter(Boolean),
                }))
              }
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option + index}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Valid for Users (emails)"
                  placeholder="Type email and press Enter"
                  error={Boolean(errors.valid_for_users)}
                  helperText={errors.valid_for_users}
                />
              )}
            />
          )}
          {showOrgEmails && (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={values.valid_for_orgs}
              onChange={(_, newValue) =>
                setValues((prev) => ({
                  ...prev,
                  valid_for_orgs: newValue
                    .map((item) => item.trim())
                    .filter(Boolean),
                }))
              }
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option + index}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Valid for Organizations (emails)"
                  placeholder="Type email and press Enter"
                  error={Boolean(errors.valid_for_orgs)}
                  helperText={errors.valid_for_orgs}
                />
              )}
            />
          )}
          <TextField
            name="max_usage_per_account"
            label="Max Usage/Account"
            type="number"
            inputProps={{ min: 0 }}
            value={values.max_usage_per_account}
            onChange={handleChange}
            error={Boolean(errors.max_usage_per_account)}
            helperText={errors.max_usage_per_account}
            fullWidth
          />
          <TextField
            name="max_usage"
            label="Max Usage"
            type="number"
            inputProps={{ min: 0 }}
            value={values.max_usage}
            onChange={handleChange}
            error={Boolean(errors.max_usage)}
            helperText={errors.max_usage}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              name="status"
              value={values.status}
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  status: e.target.value as any,
                }))
              }
              error={Boolean(errors.status)}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </Select>
            {errors.status && (
              <Box sx={{ color: "error.main" }}>{errors.status}</Box>
            )}
          </FormControl>
          <TextField
            name="tnc"
            label="Terms & Conditions"
            value={values.tnc}
            onChange={handleChange}
            error={Boolean(errors.tnc)}
            helperText={errors.tnc}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            name="start_at"
            label="Start Date & Time"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={values.start_at}
            onChange={handleChange}
            error={Boolean(errors.start_at)}
            helperText={errors.start_at}
            fullWidth
          />
          <TextField
            name="expire_at"
            label="Expire Date & Time"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={values.expire_at}
            onChange={handleChange}
            error={Boolean(errors.expire_at)}
            helperText={errors.expire_at}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Update Promo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdatePromo;
